import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';

import { fitToExtent } from '../map';

export const noneSelectValues = [{title: "None", titleNepali: "--", code: -1}];

const capitalize = text => text.slice(0,1).toUpperCase() + text.slice(1).toLowerCase();

/*
Base Nav Component is an abstract class representing a component in the mapNavigator.
It contains only the logic for storing a innercomponent and assigning itself as the
parent component of the innerComponent. And also, the before and afterselect functions

--> All concrete subclasses of BaseNavComponent has to implement
select method 
--> Inherited by Country Class (Component), the abstract class LayerNavComponent & it's
subclasses State, District & Municipality (Components)
*/
export class BaseNavComponent
{
    constructor({innerComponent})
    {
        this.innerComponent = innerComponent;
        // if innerComponent is passed, set it's parentComponent to this component
        innerComponent && (innerComponent.parentComponent = this);
        this.currentCode = -1;
        this.beforeSelect = async () => {};
        this.afterSelect = async () => {};
    }

    // handle select region. function with defered implementations
    // must have the same arguments for uniformity
    async select(code, {reselect=false}={})
    {
        throw `child classes of baseNavComponent must implement select method with args. Params => ${code} ${reselect}`
    }

    isSelected()
    {
        return this.currentCode!==0 && this.currentCode!==-1;
    }

    getInnermostSelectedComponent()
    {
        if (this.innerComponent && this.innerComponent.isSelected())
        {
            return this.innerComponent.getInnermostSelectedComponent();
        }
        return this;
    }
}

/*
LayerNavComponent is an abstract class representing a component in the mapNavigator
that has a layer(state, district, municipality & soloMunicipality).
It inherits from LayerNavComponent & holds the logic for
Most of the common logics State, District & Municipality Selection are written here.

--> All concrete subclasses of LayerNavComponent has to implement
getAllFeaturesFromCache() and may override any functions or attributes when necessary
*/
export class LayerNavComponent extends BaseNavComponent
{
    constructor({layer, codeField, labelField, innerComponent, linkedSources,})
    {
        console.assert(layer && codeField && labelField);
        super({innerComponent}); // calling BaseNavComponents constructor
        this.layer = layer;
        this.source = layer.getSource();
        this.codeField = codeField;
        this.labelField = labelField;
        this.linkedSources = linkedSources || [];
        this.pluralName = 'Regions'; // override in child when necessary, used in get selectoptions
    }

    addFeatures(features)
    {
        this.source.addFeatures(features);
        // if component has any linked source that must sync with the components
        // they are handled here. (similar logic is in clearFeatures)
        const makeCentroidFeature = ftr => new Feature({
            ...ftr.getProperties(),
            geometry: new Point([ftr.get('cx'), ftr.get('cy')]),
        });
        this.linkedSources.forEach( src =>
        {
            (src.get('isCentroid')===true) ? // if linked src is pt source, add centroid
                features.forEach( ftr => src.addFeature( makeCentroidFeature(ftr) ) )
                : 
                src.addFeatures(features); // else add the polygon features
        });
    }

    // function to link the api call of all features to the component  
    async getAllFeaturesFromCache()
    {
        throw "getAllFeaturesFromCache not implemented";
    }

    // method to get parent code for when all features (all states or all districts ,etct ) in the
    // component is selected, to perform upperlevel selection
    getParentCode()
    {
        if (!this.parentComponent) throw "No parent component";
        return this.parentComponent.currentCode;
    }

    // method to perform selection takes code as params
    async select(code, {reselect=false}={})
    {
        if (code===-1) return;
        if (!reselect && this.currentCode===code) return;
        
        if (code===0) // checking 
        {
            await this.parentComponent.select(this.getParentCode(), {reselect: true});
            return;
        }
        // code!==0 means inner components need to be select
        this.currentCode = code;
        this.beforeSelect(code);

        this.innerComponent.clearFeatures({recursive: true});
        const innerFeatures = (await this.innerComponent.getAllFeaturesFromCache()).filter(feature => feature.get(this.codeField)===code);
        this.innerComponent.addFeatures(innerFeatures);
        this.innerComponent.currentCode = 0;
        fitToExtent(this.innerComponent.source.getExtent());

        await this.afterSelect(code);
        return true;
    }

    // function to clear features. also clear features of all linked sources
    // when recursive is true, clears features of all innerComponents recursively
    clearFeatures({recursive=false})
    {
        this.currentCode = -1;
        this.source.clear();
        this.linkedSources.forEach( src => src.clear() );
        if (this.innerComponent && recursive)
        {
            this.innerComponent.clearFeatures({recursive: true});
        }
    }

    // returns {title, code} of all current features
    getSelectionOptions()
    {
        const features = this.source.getFeatures();

        if (!features || features.length===0) return noneSelectValues;
        const allFeatureInfo = features.map( feature =>
        ({
            title: capitalize(feature.get(this.labelField)),
            code: feature.get(this.codeField),
        }));
        return [
            {title: `All ${this.pluralName}`, code: 0},
            ...allFeatureInfo.sort((item1, item2) => item1.title < item2.title ? -1 : 1),
        ];
    }

    // function to add components layer to map
    // if recursive is passed as true, layers of all innerComponents is also added to the map
    // recursively
    addTo(map, {recursive=false})
    {
        map.addLayer(this.layer);
        if (this.innerComponent && recursive) this.innerComponent.addTo(map, {recursive: true})
    }
}
